import { Link } from "react-router-dom";
import { useState, type SyntheticEvent } from "react";
import type { BrandSummary } from "@shared/types";
import { TagPill } from "./TagPill";
import { scoreLabel } from "../lib/formatting";
import { classifyLoadedBrandImage, looksLikeLogoUrl } from "../lib/imageHeuristics";

type ImageState = "loading" | "photo" | "logo" | "failed";

export function BrandCard({ brand }: { brand: BrandSummary }) {
  const imageUrl = normalizeImageUrl(brand.ogImageUrl);
  const [imgState, setImgState] = useState<ImageState>(
    imageUrl ? (looksLikeLogoUrl(imageUrl) ? "logo" : "loading") : "failed",
  );

  const region = brand.region ?? brand.country;
  const freshLabel = formatFreshness(brand.publishedAt);
  const isHot = Boolean(imageUrl) && (brand.scores.discovery ?? 0) >= 80 && (brand.scores.mainstream ?? 100) <= 38;
  const crowd = brand.crowdSignals?.counts;
  const crowdTotal = crowd
    ? crowd.early + crowd.would_wear + crowd.mainstream + crowd.not_it
    : 0;
  const crowdPositive = crowd ? crowd.early + crowd.would_wear : 0;
  const crowdNegative = crowd ? crowd.mainstream + crowd.not_it : 0;
  const crowdNet = crowdPositive - crowdNegative;
  const crowdLabel =
    crowdTotal === 0 ? "Needs votes"
    : crowdNet > 0 ? "Crowd pick"
    : crowdNet < 0 ? "Divisive"
    : "Split signal";

  // Detect logo-style images on load. Two signals:
  //   1) Very wide/tall aspect or small native size  → wordmark/icon.
  //   2) Transparent or dark monochrome art → logo-ish on the dark UI.
  // Pixel sampling needs CORS; if blocked we silently fall back to "photo".
  const onLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    setImgState(classifyLoadedBrandImage(e.currentTarget));
  };

  const mediaClass =
    imgState === "failed" ? "brand-card__media brand-card__media--text"
    : imgState === "logo" ? "brand-card__media brand-card__media--logo"
    : "brand-card__media";

  return (
    <Link to={`/brands/${brand.slug}`} className="brand-card">
      <div className={mediaClass}>
        {isHot && <span className="brand-card__badge">▲ Heat</span>}
        {imgState === "failed"
          ? <span className="brand-card__fallback-title">{brand.name}</span>
          : <img
              src={imageUrl!}
              alt={`${brand.name} hero`}
              loading="lazy"
              referrerPolicy="no-referrer"
              onLoad={onLoad}
              onError={() => setImgState("failed")}
            />
        }
      </div>

      <div className="brand-card__body">
        <div>
          <div className="brand-card__meta">
            <span>{brand.primaryCategory?.name ?? "Brand"}{region ? ` · ${region}` : ""}</span>
            {freshLabel && <em>{freshLabel}</em>}
          </div>
          <div className="brand-card__name">{brand.name}</div>
        </div>
        {brand.shortSummary && <p className="brand-card__summary">{brand.shortSummary}</p>}
        <div className="brand-card__tags">
          {brand.topTags.slice(0, 3).map((t) => (
            <TagPill key={t.id} tag={t} linked={false} />
          ))}
        </div>
        <div className="brand-card__crowd" aria-label="Crowd signal">
          <div className="brand-card__crowd-head">
            <span>{crowdLabel}</span>
            <strong>{crowdTotal}</strong>
          </div>
          <b>Early {crowd?.early ?? 0}</b>
          <b>Wear {crowd?.would_wear ?? 0}</b>
          <small>Open to vote</small>
        </div>
        <div className="brand-card__score">
          <span>Disc <b>{scoreLabel(brand.scores.discovery, "discovery")}</b></span>
          <span>Mainstream <b>{scoreLabel(brand.scores.mainstream, "mainstream")}</b></span>
          <span>Momentum <b>{scoreLabel(brand.scores.momentum, "momentum")}</b></span>
        </div>
      </div>
    </Link>
  );
}

function normalizeImageUrl(url: string | null): string | null {
  if (!url) return null;
  return url.startsWith("http://") ? `https://${url.slice("http://".length)}` : url;
}

function formatFreshness(value: string | null): string | null {
  if (!value) return null;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return null;
  const days = Math.max(0, Math.floor((Date.now() - time) / 86_400_000));
  if (days === 0) return "Today";
  if (days === 1) return "1d ago";
  if (days < 7) return `${days}d ago`;
  if (days < 14) return "Last week";
  return null;
}
